import type { SmartDecisionMinute, SmartOverviewTarget } from '@nadoutong/contracts';
import { useT } from '../../i18n/index.ts';
import { stackedShareFromDecisions } from './smartRouting.ts';

const TONE_FILL = [
  'fill-signal',
  'fill-ok',
  'fill-warn',
  'fill-bad',
  'fill-signal-deep',
  'fill-paper-mid',
] as const;

const TONE_SWATCH = [
  'bg-signal',
  'bg-ok',
  'bg-warn',
  'bg-bad',
  'bg-signal-deep',
  'bg-paper-mid',
] as const;

const WIDTH = 640;
const HEIGHT = 148;
const PAD_X = 8;
const PAD_TOP = 8;
const PAD_BOTTOM = 22;
const INNER_H = HEIGHT - PAD_TOP - PAD_BOTTOM;

function labelForKey(key: string, targets: readonly SmartOverviewTarget[]): string {
  const match = targets.find((row) => row.target_hash === key);
  if (match?.channel_slug !== undefined && match.channel_slug !== null && match.channel_slug.length > 0) {
    return match.channel_slug;
  }
  return key.length > 10 ? `${key.slice(0, 8)}…` : key;
}

/**
 * Stacked share over `decisions_per_minute`. Light SVG rather than TrendChart:
 * that helper is a shared-scale line/bar chart, not a 100% stack.
 */
export function TenantRoutingSmartShareChart({
  skuId,
  minutes,
  targets,
  ready,
}: {
  readonly skuId: string;
  readonly minutes: readonly SmartDecisionMinute[];
  readonly targets: readonly SmartOverviewTarget[];
  /** False while the first overview has not succeeded — do not draw the empty state. */
  readonly ready: boolean;
}): JSX.Element | null {
  const t = useT();
  const stack = stackedShareFromDecisions(minutes);

  if (!ready) return null;

  if (stack.labels.length === 0 || stack.keys.length === 0) {
    return (
      <p
        className="m-0 px-4 pb-3 text-dense text-paper-low"
        data-testid={`tenant-routing-smart-share-empty-${skuId}`}
      >
        {t('tenant_routing.smart.chart.empty')}
      </p>
    );
  }

  const innerW = WIDTH - PAD_X * 2;
  const barW = innerW / stack.labels.length;
  const gap = Math.min(2, barW * 0.12);

  return (
    <div className="flex flex-col gap-2 px-4 pb-3" data-testid={`tenant-routing-smart-share-chart-${skuId}`}>
      <span className="text-label text-paper-low">{t('tenant_routing.smart.chart.title')}</span>
      <svg
        viewBox={`0 0 ${String(WIDTH)} ${String(HEIGHT)}`}
        className="h-36 w-full"
        role="img"
        aria-label={t('tenant_routing.smart.chart.title')}
      >
        {stack.labels.map((label, index) => {
          let y = PAD_TOP + INNER_H;
          const x = PAD_X + index * barW + gap / 2;
          const width = Math.max(1, barW - gap);
          return (
            <g key={`${label}-${String(index)}`}>
              {stack.keys.map((key, seriesIndex) => {
                const series = stack.values[key];
                const share = series?.[index] ?? 0;
                if (share <= 0) return null;
                const h = share * INNER_H;
                y -= h;
                const fill = TONE_FILL[seriesIndex % TONE_FILL.length] ?? 'fill-paper-mid';
                return (
                  <rect
                    key={key}
                    x={x}
                    y={y}
                    width={width}
                    height={h}
                    className={fill}
                  />
                );
              })}
              {index === 0 || index === stack.labels.length - 1 || index % Math.max(1, Math.floor(stack.labels.length / 6)) === 0 ? (
                <text
                  x={x + width / 2}
                  y={HEIGHT - 6}
                  textAnchor="middle"
                  className="fill-paper-low font-mono text-micro"
                >
                  {label}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
      <div className="flex flex-wrap gap-3">
        {stack.keys.map((key, index) => (
          <span key={key} className="inline-flex items-center gap-1.5 text-micro text-paper-mid">
            <span
              aria-hidden="true"
              className={`size-2 rounded-sm ${TONE_SWATCH[index % TONE_SWATCH.length] ?? 'bg-paper-mid'}`}
            />
            <span className="font-mono">{labelForKey(key, targets)}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
